import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { Search, ShoppingBag, Heart } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { SearchOverlay } from "./SearchOverlay";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_c129d2ac-2209-4214-9ac4-af6d02f0e7d1/artifacts/e10f2m7m_hf_20260429_103436_b903d0ed-1980-49ff-95c4-cf93c75881ec.png";

const navItems = [
  { to: "/", label: "Home", end: true, testid: "nav-home" },
  { to: "/shop", label: "Shop", testid: "nav-shop" },
  { to: "/find-us", label: "Find Us", testid: "nav-find-us" },
  { to: "/about", label: "Story", testid: "nav-story" },
  { to: "/account", label: "Account", testid: "nav-account" },
];

export const Header = () => {
  const cart = useCart();
  const wishlist = useWishlist();
  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-jow-cream/80 border-b border-jow-navy/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="header-logo-link"
            className="flex items-center gap-3"
          >
            <img
              src={LOGO_URL}
              alt="JewelleryOnWheels"
              className="h-11 w-11 object-contain"
              data-testid="header-logo-img"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display text-lg font-semibold text-jow-navy tracking-tight">
                JewelleryOnWheels
              </span>
              <span className="label-overline text-jow-navy/55">
                Beauty That Comes To You
              </span>
            </div>
          </Link>

          <nav
            data-testid="primary-nav"
            className="hidden md:flex items-center gap-8"
          >
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `brand-link font-sans text-[13px] uppercase tracking-[0.22em] transition-colors ${
                    isActive ? "text-jow-pink" : "text-jow-navy/80 hover:text-jow-navy"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <IconButton
              testid="header-search"
              ariaLabel="Search"
              onClick={() => setSearchOpen(true)}
            >
              <Search size={18} />
            </IconButton>
            <IconLink
              to="/wishlist"
              testid="header-wishlist"
              ariaLabel="Wishlist"
              count={wishlist.count}
            >
              <Heart size={18} />
            </IconLink>
            <IconLink
              to="/cart"
              testid="header-cart"
              ariaLabel="Cart"
              count={cart.cart.count}
            >
              <ShoppingBag size={18} />
            </IconLink>
            <Link
              to="/login"
              data-testid="header-cta-login"
              className="btn-primary-jow text-[11px] hidden sm:inline-flex ml-2"
            >
              Sign In
            </Link>
          </div>
        </div>

        {/* mobile nav */}
        <nav
          data-testid="mobile-nav"
          className="md:hidden flex items-center pb-4 overflow-x-auto"
        >
          {navItems.map((item) => (
            <NavLink
              key={`m-${item.to}`}
              to={item.to}
              end={item.end}
              data-testid={`m-${item.testid}`}
              className={({ isActive }) =>
                `font-sans text-[11px] uppercase tracking-[0.2em] whitespace-nowrap mr-5 ${
                  isActive ? "text-jow-pink" : "text-jow-navy/70"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
};

function IconButton({ children, onClick, testid, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-label={ariaLabel}
      className="relative w-10 h-10 inline-flex items-center justify-center text-jow-navy hover:text-jow-pink transition"
    >
      {children}
    </button>
  );
}

function IconLink({ to, children, testid, ariaLabel, count }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      aria-label={ariaLabel}
      className="relative w-10 h-10 inline-flex items-center justify-center text-jow-navy hover:text-jow-pink transition"
    >
      {children}
      {count > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-jow-pink text-white text-[10px] font-bold flex items-center justify-center"
          data-testid={`${testid}-count`}
        >
          {count}
        </span>
      )}
    </Link>
  );
}
